// supervisor-mission-run-startup.ts
//
// Split out of supervisor-mission-run.ts (was 202 lines, over the 200-line
// budget — review 260909-p6-m2 INFO). Owns the two startup-only concerns
// `runMissionToIdle` needs BEFORE its main dispatch loop: probing the local
// `claude` binary's version (for the §18 beforeDispatch gate) and reclaiming
// any ticket a previous crashed/killed `run` left stuck "in_progress" (Bug C
// fix, dogfood P10, ME-2026-0097d9) — done before ensureBootstrapCommit so a
// stale binding file never gets swept into that commit (see the long comment
// this replaces in supervisor-mission-run.ts for the full ordering rationale).
// N9 (phase 05): shared probe from the neutral module — never from the
// CLI-wiring layer's own copy (this is the runtime supervisor layer).
// Re-exported under the original name so `supervisor-mission-run.ts`'s
// import keeps compiling.
import { probeClaudeVersion as defaultProbeClaudeVersion } from "./claude-version-probe.js";
import type { GitRunner } from "./commit-owner.js";
import { isDirectiveProcessed, listInboxDirectiveIds, readDirective } from "./directive-inbox.js";
import { MissionStore } from "./mission-store.js";
import { openMissionStoreReadOnly } from "./mission-store-read-only.js";
import {
  applyOrphanedTicketRecoveries,
  cleanupStaleOrphanedBinding,
  detectOrphanedInProgressTickets,
  type OrphanedTicketRecovery,
} from "./orphaned-ticket-reconciler.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { readAttemptStateFile } from "./supervisor-attempt-state.js";
import {
  applyAbortConfirmDirective,
  applyCliIssuedAbortDirective,
} from "./supervisor-halt-directive-step.js";
import { archiveForensicsBeforeReclaimReset } from "./supervisor-orphan-reclaim-forensics.js";
import { recoverOwnedCheckout } from "./supervisor-owned-checkout-recovery.js";
import { loadTicketsFromRepo } from "./supervisor-ticket-projection.js";

export { defaultProbeClaudeVersion };

export interface ReconcileOrphanedTicketsAtStartupInput {
  store: MissionStore;
  controlRoot: string;
  missionWorkspace: string;
  executionRepo: string;
  now: () => Date;
  /**
   * NEW-2 fix (dogfood recheck 260925): supplied => a RECLAIMABLE orphan
   * resets the worktree to HEAD before this returns (see doc comment below).
   * Optional so existing callers/tests (audit M3/M4 suite) that only care
   * about detection keep compiling unchanged.
   */
  gitRunner?: GitRunner;
}

/**
 * Detects + applies orphan recoveries at `run` startup, and cleans up the
 * stale binding file BEFORE the caller's bootstrap commit stages the whole
 * worktree — a stale binding left in place at that point would get
 * COMMITTED into git, un-removable by a later `git reset --hard HEAD`
 * (supervisor-owned-checkout-recovery.ts runs every tick and would just
 * restore it from HEAD). Cheap no-op when nothing is stuck.
 *
 * NEW-2 fix (dogfood recheck 260925): a SIGKILLed attempt's uncommitted
 * SOURCE diff also stays in the worktree, and the caller's
 * `ensureBootstrapCommit` (manifestPaths `["."]`) stages the WHOLE worktree
 * right after this returns. Measured on real missions D/D2: that leftover
 * diff got committed under the bootstrap ref `[T-0000]` instead of the
 * ticket's own retry commit. `recoverOwnedCheckout` (`git reset --hard HEAD`
 * + `git clean -fd`, keeping runtime/staging dirs) already exists for this
 * shape (supervisor-pre-dispatch-checks.ts runs it before the NEXT dispatch
 * of a ticket) — this runs the SAME reset one step earlier, before bootstrap
 * can stage the leftover. Reclaimable recoveries only: `binding_corrupt`
 * must never auto-touch the worktree (fail-closed, forensics only).
 */
export async function reconcileOrphanedTicketsAtStartup(
  input: ReconcileOrphanedTicketsAtStartupInput,
): Promise<OrphanedTicketRecovery[]> {
  const tickets: Ticket[] = await loadTicketsFromRepo(input.executionRepo);
  const recoveries = await detectOrphanedInProgressTickets({
    tickets,
    ticketStatus: input.store.state.tickets,
    missionWorkspace: input.missionWorkspace,
    now: input.now(),
    // Req 9 (NEW-3): lets the supervisor_crash evidence name an unlock that
    // already killed the worker, instead of staying evidence-free.
    controlRoot: input.controlRoot,
  });
  // Audit 2026-09-14 (M3): `binding_corrupt` is EXCLUDED from this early
  // cleanup. The corrupt file is the evidence — orphaned-ticket-recovery-
  // applier.ts reads its raw bytes into the forensics archive a moment later,
  // and it cannot archive a file startup already deleted. Measured before the
  // fix: the archive recorded "(không đọc được)" instead of the contents, so a
  // possibly-tampered binding left no trace of what it actually held.
  //
  // Reclaimable recoveries keep their cleanup: a leftover binding there would
  // otherwise read as a scope violation on the retry (Bug E).
  const reclaimable = recoveries.filter((r) => r.reason !== "binding_corrupt");
  if (reclaimable.length > 0) {
    await cleanupStaleOrphanedBinding(input.missionWorkspace);
    if (input.gitRunner) {
      // O-2 fix (dogfood recheck 260925 follow-up): archive the diff the
      // reset below is about to destroy, BEFORE it runs — see
      // supervisor-orphan-reclaim-forensics.ts's header.
      const attemptState = await readAttemptStateFile(input.controlRoot);
      await archiveForensicsBeforeReclaimReset({
        store: input.store,
        controlRoot: input.controlRoot,
        missionWorkspace: input.missionWorkspace,
        ticketIds: reclaimable.map((r) => r.ticketId),
        attemptState,
      });
      // NEW-2 fix: reset BEFORE bootstrap can stage the leftover diff. Uses
      // executionRepo (== missionWorkspace in production, per
      // supervisor-mission-run.ts's own comment) — the mission's OWN worktree,
      // never the human's source checkout.
      await recoverOwnedCheckout(input.gitRunner, input.executionRepo);
    }
  }
  return recoveries;
}

/**
 * Journal + physical_runs bump happen AFTER bootstrap (needs a committed
 * HEAD to journal against) but the binding cleanup above ran BEFORE it (see
 * `reconcileOrphanedTicketsAtStartup`'s comment) — this is itself a no-op
 * re-cleanup call, cheap and idempotent.
 */
export async function applyOrphanedRecoveriesAfterBootstrap(
  store: MissionStore,
  controlRoot: string,
  missionWorkspace: string,
  recoveries: OrphanedTicketRecovery[],
): Promise<void> {
  await applyOrphanedTicketRecoveries(store, controlRoot, missionWorkspace, recoveries);
}

/**
 * [B1] ABORTED is permanent (mission-state-reducer.ts's sticky rule) — a
 * mission already at that status must never even attempt startSupervisor
 * again (no lock, no effective-config load, no §18 gate, 0 dispatch).
 * Lock-free (openMissionStoreReadOnly) so this check never contends with a
 * still-running writer for the same mission.
 */
export async function isMissionAbortedAtStartup(controlRoot: string): Promise<boolean> {
  const ro = await openMissionStoreReadOnly(controlRoot);
  try {
    return ro.state.status === "ABORTED";
  } finally {
    await ro.close();
  }
}

/**
 * [B1] A queued `abort_confirm` must apply even when `run` is about to block
 * at startup (config_invalid/doctor_blocked/§18 before-dispatch) — those
 * three early-returns happen BEFORE the dispatch loop (and its halt-step)
 * ever runs, so without this pass a mission stuck on quiet-hours/doctor/bad
 * config could never be aborted until the block itself was fixed. Runs
 * lock-free FIRST: no `abort_confirm` pending ⇒ return false immediately,
 * without opening the store — the 3 early-return branches keep today's
 * behaviour untouched in the common case. `abort` directives carrying a
 * `challenge_id` are applied FIRST (journal `abort_challenge_issued`) so a
 * confirm landing in the SAME startup pass has a challenge event on record.
 */
export async function applyQueuedAbortConfirmAtStartup(controlRoot: string): Promise<boolean> {
  const ids = await listInboxDirectiveIds(controlRoot);
  const pending = [];
  for (const id of ids) {
    if (await isDirectiveProcessed(controlRoot, id)) continue;
    const directive = await readDirective(controlRoot, id);
    if (directive && (directive.kind === "abort" || directive.kind === "abort_confirm")) {
      pending.push(directive);
    }
  }
  pending.sort(
    (a, b) =>
      a.received_at.localeCompare(b.received_at) || a.directive_id.localeCompare(b.directive_id),
  );
  const confirms = pending.filter((d) => d.kind === "abort_confirm");
  if (confirms.length === 0) return false;

  const cliAborts = pending.filter(
    (d) => d.kind === "abort" && (d.payload as Record<string, unknown>).challenge_id !== undefined,
  );
  const store = await MissionStore.open(controlRoot);
  try {
    for (const directive of cliAborts) {
      await applyCliIssuedAbortDirective({ store, controlRoot, directive });
    }
    let abortedAny = false;
    for (const directive of confirms) {
      const aborted = await applyAbortConfirmDirective({ store, controlRoot, directive });
      abortedAny = abortedAny || aborted;
    }
    return abortedAny;
  } finally {
    await store.close();
  }
}
